import React, { useCallback, useState } from "react";
import { defineMessage } from "react-intl";
import "@glideapps/glide-data-grid/dist/index.css";
import { DataEditor, CompactSelection, GridCellKind } from "@glideapps/glide-data-grid";
import { useTheme, useMediaQuery } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import "../../alerts/InfoMessage.js";
import "../../alerts/NoAccessInfoMessage.js";
import "../../alerts/Message.style.js";
import { LocalizedText } from "../../i18n/LocalizedText.js";
import { InfoAlert } from "../../alerts/InfoAlert.js";
import "../../alerts/InfoMessageHoc.js";
import StyledPagination from "../../pagination/StyledPagination.js";
import { getInvalidDataCellRenderer } from "./cells/GlideInvalidDataCell.js";
import { renderSelectAllCell, renderHeaderCell } from "./cells/headerCell.js";
import "moment-timezone";
import "./cells/GlideTextCell.js";
import { rowHeight } from "./types.js";
import "../../icons/Icon.js";
import "../../icons/ExpandMoreIcon.js";
import { GlideGridTooltip } from "./GlideGridTooltip.js";
const noRows = defineMessage({
  id: "GlideGrid_noRows",
  defaultMessage: "No Rows"
});
const useClasses = makeStyles()((theme, { maxHeight }) => {
  const topMargin = theme.custom.gridHeaderHeight + theme.custom.borderWidth;
  return {
    glideGrid: {
      position: "relative",
      flex: 1
    },
    glideGridFullHeight: {
      "& .gdg-wmyidgi": {
        height: "100%"
      }
    },
    glideGridMaxHeight: {
      maxHeight: `${maxHeight + 16}px !important`
    },
    glideGridBorder: {
      "&.gdg-wmyidgi": {
        border: `1px solid ${theme.palette.secondary.light}`,
        borderRadius: theme.custom.defaultBorderRadius * 2
      }
    },
    glideGridNoRowsMessageContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      bottom: theme.custom.scrollBarHeight,
      position: "absolute",
      top: topMargin,
      backgroundColor: "#fbfbfb"
    },
    // Added to hide cell outline when grid is empty. Can be broken with new version of glide grid
    glideGridNoRows: {
      "& canvas:first-of-type": {
        opacity: 0
      }
    }
  };
});
function GlideGrid({
  rows,
  columns,
  rawModels,
  sortModel,
  page,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  totalRows,
  isTotalCountUnknown,
  freezeColumns,
  pinnedRow,
  pinnedRawModel,
  emptyMessage,
  hidePagination,
  gridFullHeight = true,
  maxVisibleRows,
  hasBorder,
  selection,
  isRowSelectable,
  onSelect,
  onPageSizeChange,
  onPageChange,
  onSortChange
}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const maxHeight = maxVisibleRows ? (maxVisibleRows + 1) * rowHeight + (hasBorder ? 2 : 0) : null;
  const { classes, cx } = useClasses({ maxHeight });
  const isSelectionEnabled = selection && !selection?.disabled;
  const finalRows = pinnedRow ? [pinnedRow, ...rows] : rows;
  const finalRawModels = pinnedRawModel ? [pinnedRawModel, ...rawModels] : rawModels;
  const allRowsSelected = React.useMemo(() => {
    if (!isSelectionEnabled) return false;
    const selectableRows = finalRows.filter((row) => !isRowSelectable || isRowSelectable(row));
    const selectedIdSet = new Set(selection?.selectedIds ?? []);
    return selectableRows.length > 0 && selectableRows.length === selectedIdSet.size && selectableRows.every((row) => selectedIdSet.has(selection?.idGetter(row)));
  }, [selection?.selectedIds, isRowSelectable]);
  const getCellContent = useCallback(
    (cell) => {
      const [col, row] = cell;
      return columns[col].cell.getCellContent(finalRows[row], finalRawModels[row], !!pinnedRow && row === 0);
    },
    [JSON.stringify(finalRows), columns?.map((c) => c.id)?.join()]
  );
  const drawHeader = React.useCallback(
    (args) => {
      const { ctx, rect, columnIndex, theme } = args;
      if (columnIndex === -1 && isSelectionEnabled) {
        const hasSelectedRows = (selection?.selectedIds?.length ?? 0) > 0;
        renderSelectAllCell({
          ctx,
          rect,
          theme,
          muiTheme,
          status: allRowsSelected ? "selected" : hasSelectedRows ? "indeterminate" : "notSelected"
        });
      } else {
        renderHeaderCell({ ctx, rect, column: columns[columnIndex], glideTheme: theme, muiTheme, sortModel });
      }
    },
    [sortModel?.[0]?.field, sortModel?.[0]?.sort, columns?.map((c) => c.id)?.join(), allRowsSelected, selection?.selectedIds?.length]
  );
  const handleRowThemeOverride = useCallback(
    (row) => {
      let result = void 0;
      if (pinnedRow && row === 1) {
        result = {
          horizontalBorderColor: muiTheme.custom.palette.content.borderDark
        };
      }
      return result;
    },
    [!pinnedRow]
  );
  const [tooltipContent, setTooltipContent] = useState(null);
  const [gridSelectionState, setGridSelectionState] = useState(void 0);
  function handlePageChange(page2) {
    onPageChange(page2 + 1);
    onSelect([]);
  }
  function handleSortChange(colIndex) {
    const clickedColumn = columns[colIndex];
    if (clickedColumn?.sortable) {
      const columnId = clickedColumn?.id;
      const direction = sortModel?.[0]?.field === columnId && sortModel?.[0]?.sort === "desc" ? "asc" : "desc";
      onSortChange([{ field: columnId, sort: direction }]);
    }
  }
  function handleItemHovered(args) {
    switch (args?.kind) {
      case "cell": {
        const [col, row] = args.location;
        const cell = columns[col].cell.getCellContent(finalRows[row], finalRawModels[row]);
        if (cell?.kind === GridCellKind.Custom && cell?.data) {
          const data = cell.data;
          const TooltipContent = data.tooltip;
          setTooltipContent({
            value: data.value ? /* @__PURE__ */ React.createElement(TooltipContent, { value: data.value }) : null,
            bounds: args?.bounds
          });
        } else {
          closeTooltip();
        }
        break;
      }
      case "header": {
        const columnIndex = args?.location?.[0];
        setTooltipContent({ bounds: args?.bounds, value: columns[columnIndex].columnTooltip });
        break;
      }
      default: {
        closeTooltip();
        break;
      }
    }
  }
  function closeTooltip() {
    setTooltipContent(null);
  }
  function getSelectedRows() {
    const selectedIds = selection?.selectedIds ?? [];
    const idGetter = selection?.idGetter;
    if (!selectedIds.length || !idGetter) return CompactSelection.empty();
    const selectedIdSet = new Set(selectedIds);
    const indexes = finalRows.map((row, index) => selectedIdSet.has(idGetter(row)) ? index : -1).filter((i) => i >= 0);
    return indexes.reduce((sel, i) => sel.add(i), CompactSelection.empty());
  }
  function handleGridSelectionChange(gridSelection) {
    setGridSelectionState(gridSelection);
    const indices = gridSelection.rows.toArray();
    const isHeaderCheckboxClicked = gridSelection.current?.cell?.[0] === 0 && gridSelection.current?.cell?.[1] === 0;
    const isDataCellClicked = indices.length === 0 && gridSelection.current !== void 0 && !isHeaderCheckboxClicked;
    if (isDataCellClicked) {
      return;
    }
    let selectableIds = [];
    if (!allRowsSelected || indices.length !== finalRows.length) {
      selectableIds = indices.filter((i) => !isRowSelectable || isRowSelectable(finalRows[i])).map((i) => selection?.idGetter(finalRows[i]));
    }
    onSelect(selectableIds);
  }
  return rows.length || !emptyMessage ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: cx(classes.glideGrid, gridFullHeight && classes.glideGridFullHeight) }, /* @__PURE__ */ React.createElement(GlideGridTooltip, { value: tooltipContent?.value, bounds: tooltipContent?.bounds }, /* @__PURE__ */ React.createElement(
    DataEditor,
    {
      theme: {
        bgHeader: muiTheme.palette.background.paper,
        bgHeaderHovered: muiTheme.palette.background.paper,
        bgHeaderHasFocus: muiTheme.palette.background.paper,
        cellHorizontalPadding: parseInt(muiTheme.spacing(2)),
        borderColor: muiTheme.palette.secondary.light,
        accentLight: muiTheme.palette.background.paper,
        accentColor: muiTheme.palette.primary.main
      },
      customRenderers: [
        getInvalidDataCellRenderer(muiTheme),
        ...columns.map((c) => c.cell.getRenderer(muiTheme))
      ],
      columns,
      rows: finalRows.length,
      getCellContent,
      freezeColumns: isMobile ? freezeColumns?.mobile : freezeColumns?.desktop,
      minColumnWidth: 50,
      rowHeight,
      headerHeight: rowHeight,
      width: "100%",
      onHeaderClicked: handleSortChange,
      onItemHovered: handleItemHovered,
      columnSelect: "none",
      getCellsForSelection: true,
      getRowThemeOverride: handleRowThemeOverride,
      drawHeader,
      className: cx({
        [classes.glideGridNoRows]: !finalRows.length,
        [classes.glideGridMaxHeight]: Boolean(maxVisibleRows),
        [classes.glideGridBorder]: hasBorder
      }),
      smoothScrollX: true,
      smoothScrollY: true,
      rowSelect: "multi",
      rowSelectionMode: "multi",
      rowMarkers: isSelectionEnabled ? {
        kind: "checkbox-visible",
        width: 50
      } : "none",
      gridSelection: isSelectionEnabled ? {
        rows: getSelectedRows(),
        columns: gridSelectionState?.columns ?? CompactSelection.empty(),
        current: gridSelectionState?.current
      } : void 0,
      onGridSelectionChange: isSelectionEnabled ? handleGridSelectionChange : void 0
    }
  )), !finalRows.length ? /* @__PURE__ */ React.createElement("div", { className: classes.glideGridNoRowsMessageContainer }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(LocalizedText, { label: noRows }))) : null), !hidePagination ? /* @__PURE__ */ React.createElement(
    StyledPagination,
    {
      count: totalRows,
      page: page - 1,
      rowsPerPage: pageSize,
      rowsPerPageOptions: pageSizeOptions,
      onPageChange: handlePageChange,
      onPageSizeChange,
      isTotalCountUnknown
    }
  ) : null) : /* @__PURE__ */ React.createElement(InfoAlert, { title: emptyMessage });
}
export {
  GlideGrid,
  useClasses
};
//# sourceMappingURL=GlideGrid.js.map
